import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Hand, Plus } from 'lucide-react';
import { request } from '../../../api/client';
import { defineMessages } from '../../../i18n/define';
import { UNIT_LABELS, useT } from '../../../i18n/hooks';
import { useRequest } from '../../../api/useRequest';
import { useAnalysisContext } from '../../../analysis/context';
import { ownActorId, useOptionalAuth } from '../../../auth/context';
import { Card, Stack } from '../../ui/Card';
import { DataTable, type Column } from '../../ui/DataTable';
import { Badge } from '../../ui/Badge';
import { Icon } from '../../ui/Icon';
import { LoadError } from '../../ui/LoadError';
import { EmptyState } from '../../ui/EmptyState';
import { dateShort, ha } from '../../lib/format';
import { VoluntaryForm } from './VoluntaryForm';
import {
  MICROHA_PER_HA,
  authorLabel,
  categoryName,
  whenLabel,
  type VoluntaryList,
  type VoluntaryWork,
} from './voluntaryModel';
import './voluntary.css';

const m = defineMessages({
  colWhen: { id: 'voluntary.block.col.when', defaultMessage: 'Дата', description: 'Колонка таблицы добровольных работ: дата или период' },
  colWork: { id: 'voluntary.block.col.work', defaultMessage: 'Работа', description: 'Колонка таблицы добровольных работ: название работы и её категория' },
  colField: { id: 'voluntary.block.col.field', defaultMessage: 'Поле', description: 'Колонка таблицы добровольных работ: поле (контур)' },
  fieldPart: { id: 'voluntary.block.fieldPart', defaultMessage: '№ {no} · часть {part}', description: 'Ссылка на поле в таблице добровольных работ, когда работа на части контура; {no} — номер контура, {part} — часть' },
  fieldPlain: { id: 'voluntary.block.fieldPlain', defaultMessage: '№ {no}', description: 'Ссылка на поле в таблице добровольных работ; {no} — номер контура' },
  colFarm: { id: 'voluntary.block.col.farm', defaultMessage: 'Хозяйство', description: 'Колонка таблицы добровольных работ на карточке контура: хозяйство' },
  farmNo: { id: 'voluntary.block.farmNo', defaultMessage: '№ {id}', description: 'Название хозяйства в таблице, когда имени нет; {id} — служебный номер' },
  colArea: { id: 'voluntary.block.col.area', defaultMessage: 'Площадь', description: 'Колонка таблицы добровольных работ: площадь; единица «га» в шапке рядом' },
  areaNotNamed: { id: 'voluntary.block.areaNotNamed', defaultMessage: 'не названа', description: 'Ячейка площади: площадь работы не названа (это не ноль)' },
  colCycle: { id: 'voluntary.block.col.cycle', defaultMessage: 'Агроцикл', description: 'Колонка таблицы добровольных работ: к какому агроциклу привязана работа' },
  outsideCycle: { id: 'voluntary.block.outsideCycle', defaultMessage: 'вне агроцикла', description: 'Ячейка колонки «Агроцикл»: на дату работы у поля нет агроцикла' },
  colWho: { id: 'voluntary.block.col.who', defaultMessage: 'Внёс', description: 'Колонка таблицы добровольных работ: кто внёс запись' },
  colNote: { id: 'voluntary.block.col.note', defaultMessage: 'Комментарий', description: 'Колонка таблицы добровольных работ: комментарий' },
  write: { id: 'voluntary.block.write', defaultMessage: 'Записать работу', description: 'Кнопка в заголовке блока добровольных работ: открыть форму записи (фермер, менеджер)' },
  title: { id: 'voluntary.block.title', defaultMessage: 'Добровольные работы', description: 'Заголовок блока добровольных работ хозяйства или контура' },
  info: { id: 'voluntary.block.info', defaultMessage: 'Работы, которые хозяйство сделало само, без плана и предписания. В процент выполнения плана и в сравнение хозяйств не входят.', description: 'Подсказка (i) к блоку добровольных работ' },
  what: { id: 'voluntary.block.what', defaultMessage: 'добровольные работы', description: 'Что не загрузилось (винительный падеж, подставляется в «Не удалось загрузить …»): добровольные работы' },
  loading: { id: 'voluntary.block.loading', defaultMessage: 'Читаем добровольные работы…', description: 'Состояние загрузки блока добровольных работ' },
  emptyTitle: { id: 'voluntary.block.emptyTitle', defaultMessage: 'Записей нет', description: 'Заголовок пустого состояния блока добровольных работ' },
  emptyFarm: { id: 'voluntary.block.emptyFarm', defaultMessage: 'хозяйство пока не записывало добровольных работ', description: 'Причина пустого блока добровольных работ на странице хозяйства' },
  emptyContour: { id: 'voluntary.block.emptyContour', defaultMessage: 'на этом контуре добровольных работ не записано', description: 'Причина пустого блока добровольных работ на странице контура' },
  denom: {
    id: 'voluntary.block.denom',
    defaultMessage: '{count, plural, one {запись} few {записи} many {записей} other {записи}}',
    description: 'Слово-знаменатель под таблицей добровольных работ («показано 8 из 21 записи»); без числа',
  },
});

export type VoluntaryScope =
  | { kind: 'farm'; actorId: number }
  | { kind: 'contour'; contourId: number; /** Хозяйство, за которое можно записать с этой карточки. */ actorId?: number };

/**
 * Блок «Сделано по своей инициативе»: добровольные работы хозяйства или
 * контура — отдельно от плана и предписаний. Счёта «сделано/план» здесь нет и
 * быть не может: добровольная работа в процент выполнения не входит, а
 * хозяйства по числу записей не сравниваются.
 *
 * Кнопка «Записать работу» — только тем, кому сервер разрешает запись
 * (фермер своего хозяйства, диспетчер за хозяйство); хоким только читает.
 */
export function VoluntaryBlock({ scope }: { scope: VoluntaryScope }) {
  const t = useT();
  const analysis = useAnalysisContext();
  const auth = useOptionalAuth();
  const principal = auth?.principal ?? null;
  const [open, setOpen] = useState(false);

  const path = scope.kind === 'farm' ? `/farms/${scope.actorId}/voluntary-works` : `/contours/${scope.contourId}/voluntary-works`;
  const list = useRequest(() => request<VoluntaryList>(path, analysis.params), [path, analysis.key]);

  const roles = principal?.roles ?? [];
  // На карточке контура хозяйство, за которое пишем, — единственный землепользователь
  // (его называет страница) или, у фермера, его собственное хозяйство.
  const actorId = scope.actorId ?? (scope.kind === 'contour' ? (ownActorId(principal) ?? undefined) : undefined);
  const own = actorId !== undefined && (principal?.actors ?? []).some((actor) => actor.id === actorId);
  const canWrite = actorId !== undefined && (own ? roles.includes('farm') : roles.includes('verifier'));

  const columns: Column<VoluntaryWork>[] = [
    { key: 'when', header: t(m.colWhen), render: (row) => whenLabel(row, dateShort) },
    {
      key: 'work',
      header: t(m.colWork),
      render: (row) => (
        <span>
          {row.work_name} <Badge tone="neutral">{categoryName(row.category)}</Badge>
        </span>
      ),
    },
    {
      key: 'field',
      header: t(m.colField),
      render: (row) => (
        <Link to={`/contours/${row.contour_id}${analysis.linkSearch()}`}>
          {row.contour_part_label
            ? t(m.fieldPart, { no: String(row.contour_no), part: String(row.contour_part_label) })
            : t(m.fieldPlain, { no: String(row.contour_no) })}
        </Link>
      ),
    },
    ...(scope.kind === 'contour'
      ? [{ key: 'actor', header: t(m.colFarm), render: (row: VoluntaryWork) => row.actor_name ?? t(m.farmNo, { id: String(row.actor_id) }) } as Column<VoluntaryWork>]
      : []),
    {
      key: 'area',
      header: t(m.colArea),
      unit: t(UNIT_LABELS.ha),
      numeric: true,
      render: (row) => (row.area_microha === null ? t(m.areaNotNamed) : ha(row.area_microha / MICROHA_PER_HA)),
    },
    {
      key: 'cycle',
      header: t(m.colCycle),
      render: (row) =>
        // Фермеру экран цикла закрыт (isRouteAllowed) — у него имя цикла без ссылки.
        row.cycle_key
          ? roles.includes('farm')
            ? row.cycle_name
            : <Link to={`/cycles/${encodeURIComponent(row.cycle_key)}${analysis.linkSearch()}`}>{row.cycle_name}</Link>
          : t(m.outsideCycle),
    },
    { key: 'who', header: t(m.colWho), render: (row) => authorLabel(row) },
    { key: 'note', header: t(m.colNote), render: (row) => row.note || '—' },
  ];

  const aside = canWrite ? (
    <button type="button" className="astra-button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
      <Icon icon={Plus} size={16} /> {t(m.write)}
    </button>
  ) : null;

  return (
    <Card
      title={
        <span className="vol-title">
          <Icon icon={Hand} size={16} /> {t(m.title)}
        </span>
      }
      aside={aside}
      info={t(m.info)}
    >
      <Stack tight>
        {open && actorId !== undefined ? (
          <VoluntaryForm
            actorId={actorId}
            onBehalf={!own}
            presetContourId={scope.kind === 'contour' ? scope.contourId : undefined}
            onCancel={() => setOpen(false)}
            onSaved={() => {
              setOpen(false);
              list.reload();
            }}
          />
        ) : null}
        {list.state === 'error' && list.data === null ? (
          <LoadError what={t(m.what)} error={list.error} onRetry={list.reload} compact />
        ) : list.state === 'loading' && list.data === null ? (
          <p className="t-caption">{t(m.loading)}</p>
        ) : (list.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            compact
            title={t(m.emptyTitle)}
            reason={scope.kind === 'farm' ? t(m.emptyFarm) : t(m.emptyContour)}
          />
        ) : (
          <DataTable
            columns={columns}
            rows={list.data?.items ?? []}
            getKey={(row) => String(row.id)}
            denominator={m.denom}
          />
        )}
      </Stack>
    </Card>
  );
}
